import { stripDirection } from './directionUtils'
import type { CwCcwMode } from './types'

function relativePath(file: File): string {
  const webkit = (file as File & { webkitRelativePath?: string }).webkitRelativePath
  return webkit && webkit.length > 0 ? webkit : file.name
}

/**
 * Parent folder segment for a file inside a webkitdirectory upload.
 * Flat selection (no path) returns null.
 */
export function weldFolderFromRelativePath(relativePath: string): string | null {
  const parts = relativePath.replace(/\\/g, '/').split('/').filter(Boolean)
  if (parts.length < 2) return null
  return parts[parts.length - 2]
}

/** True when any file was picked from a folder (webkitdirectory) rather than as a flat list. */
export function selectionHasWeldFolderPath(files: File[]): boolean {
  return files.some((file) => weldFolderFromRelativePath(relativePath(file)) !== null)
}

/** Strip export suffixes such as `_ALX Logs` from a weld folder name. */
const ALX_FOLDER_SUFFIX = /[_\s]+ALX(?:[_\s]+Logs?)?$/i

export function stripAlxFolderSuffix(folderName: string): string {
  return folderName.replace(ALX_FOLDER_SUFFIX, '').trim()
}

export function weldIdFromFolderName(folderName: string, cwCcwMode: CwCcwMode): string {
  const withoutDirection =
    cwCcwMode === 'separate' || cwCcwMode === 'merged'
      ? stripDirection(folderName).baseName
      : folderName
  return stripAlxFolderSuffix(withoutDirection)
}

export interface DetectAlxWeldIdsResult {
  weldIds: string[]
  ambiguous: boolean
}

/**
 * Detect unique weld IDs from ALX folder upload paths.
 * Ported from extract_welding_data.py folder naming rules.
 */
export function detectAlxWeldIds(files: File[], cwCcwMode: CwCcwMode): DetectAlxWeldIdsResult {
  const ids = new Set<string>()
  for (const file of files) {
    if (!file.name.toLowerCase().endsWith('.txt')) continue
    const rel = relativePath(file)
    const folder = weldFolderFromRelativePath(rel)
    if (!folder) continue
    ids.add(weldIdFromFolderName(folder, cwCcwMode).trim())
  }
  const weldIds = Array.from(ids).filter(Boolean)
  return { weldIds, ambiguous: weldIds.length > 1 }
}

export function primaryAlxWeldId(files: File[], cwCcwMode: CwCcwMode): string | null {
  const { weldIds, ambiguous } = detectAlxWeldIds(files, cwCcwMode)
  if (ambiguous || weldIds.length === 0) return null
  return weldIds[0]
}

/** Group .txt files by detected weld ID (parent folder name). */
export function groupFilesByAlxWeldId(
  files: File[],
  cwCcwMode: CwCcwMode
): Map<string, File[]> {
  const groups = new Map<string, File[]>()
  for (const file of files) {
    if (!file.name.toLowerCase().endsWith('.txt')) continue
    const rel = relativePath(file)
    const folder = weldFolderFromRelativePath(rel)
    if (!folder) continue
    const weldId = weldIdFromFolderName(folder, cwCcwMode).trim()
    if (!weldId) continue
    const list = groups.get(weldId) ?? []
    list.push(file)
    groups.set(weldId, list)
  }
  return groups
}

/** True when any file path parent folder has a CW/CCW suffix. */
export function folderUploadHasCwCcwPaths(files: File[]): boolean {
  for (const file of files) {
    if (!file.name.toLowerCase().endsWith('.txt')) continue
    const folder = weldFolderFromRelativePath(relativePath(file))
    if (!folder) continue
    const { direction } = stripDirection(folder)
    if (direction) return true
  }
  return false
}
